import type { DocPageContent } from '../docs-content'

const page = {
  order: 3,
  slug: 'projects',
  title: 'Proyectos',
  description: 'Crea, configura, duplica, organiza, elimina, restaura y mueve proyectos.',
  category: 'Inicio',
  related: ['workspaces', 'editor'],
  sections: [
    {
      title: 'Crear y configurar un proyecto',
      blocks: [
        {
          kind: 'list',
          items: [
            'Crea un proyecto desde la página **Proyectos** haciendo clic en **Nuevo proyecto**.',
            'Configura el nombre del proyecto, la descripción, la resolución, la velocidad de fotogramas y el color de fondo.',
            'La resolución y la velocidad de fotogramas determinan el tamaño del video exportado y el comportamiento de la línea de tiempo, por lo que conviene elegirlas según el destino final del proyecto.',
            'Si más adelante necesitas modificar estas opciones, podrás hacerlo desde la barra de herramientas del editor.',
          ],
        },
      ],
    },
    {
      title: 'Tamaño del lienzo y relación de aspecto',
      blocks: [
        {
          kind: 'paragraph',
          text: 'El lienzo define el área visible sobre la que se compone y exporta el video. Puedes elegir su tamaño al crear el proyecto o modificarlo en cualquier momento desde el panel **Lienzo** mientras editas.',
        },
        {
          kind: 'paragraph',
          text: 'En la ventana **Nuevo proyecto**, la sección **Resolución** ofrece varios ajustes predefinidos para las plataformas más utilizadas. Cada opción muestra su resolución en píxeles y la relación de aspecto correspondiente. Si prefieres una resolución personalizada, selecciona **Personalizada** e introduce manualmente el ancho y el alto.',
        },
        {
          kind: 'table',
          headers: ['Preajuste', 'Resolución', 'Relación de aspecto'],
          rows: [
            ['YouTube 1080p', '`1920x1080`', '`16:9` horizontal'],
            ['Shorts / TikTok / Reels', '`1080x1920`', '`9:16` vertical'],
            ['Instagram cuadrado', '`1080x1080`', '`1:1` cuadrado'],
            ['Instagram vertical', '`1080x1350`', '`4:5` vertical'],
            ['Twitter/X', '`1200x675`', '`16:9` horizontal'],
            ['LinkedIn', '`1200x627`', 'Horizontal'],
            ['Personalizada', 'Valores definidos por ti', 'Cualquier proporción'],
          ],
        },
        {
          kind: 'paragraph',
          text: 'Para cambiar el tamaño del lienzo después de crear el proyecto:',
        },
        {
          kind: 'steps',
          items: [
            'Deselecciona cualquier clip para que el panel lateral muestre las propiedades del **Lienzo**.',
            'Modifica los campos **W** (ancho) y **H** (alto) para establecer las dimensiones del video en píxeles.',
            'Utiliza **Intercambiar** para invertir el ancho y el alto, por ejemplo, para convertir un proyecto horizontal en uno vertical.',
            'Pulsa **Restablecer** para volver al tamaño predeterminado de `1920x1080`.',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'Las dimensiones siempre se ajustan a números pares y pueden ir desde `320x240` hasta `7680x4320`. Al cambiar el tamaño del lienzo, los clips conservan su posición y escala actuales, por lo que quizá debas reposicionar aquellos que queden fuera del nuevo encuadre.',
        },
      ],
    },
    {
      title: 'Organizar la lista de proyectos',
      blocks: [
        {
          kind: 'list',
          items: [
            'Busca proyectos por nombre y utiliza las opciones de ordenación y filtrado para administrarlos con mayor facilidad.',
            'Las miniaturas y los metadatos te ayudan a identificar rápidamente el proyecto en el que trabajaste por última vez.',
            'La página **Proyectos** muestra el espacio de trabajo activo para que siempre sepas dónde se almacenan tus proyectos.',
            'Utiliza **Duplicar** para crear una nueva versión de un proyecto sin modificar el original.',
          ],
        },
      ],
    },
    {
      title: 'Papelera y restauración',
      blocks: [
        {
          kind: 'list',
          items: [
            'Cuando eliminas un proyecto, este se mueve a la **Papelera** en lugar de borrarse inmediatamente.',
            'Mientras permanezca allí, podrás restaurarlo en cualquier momento.',
            'La opción **Vaciar papelera** elimina de forma permanente los proyectos descartados y cualquier archivo multimedia que solo esté asociado a ellos.',
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'La eliminación permanente no puede deshacerse. Antes de vaciar la papelera, asegúrate de que realmente deseas borrar esos proyectos.',
        },
      ],
    },
    {
      title: 'Mover proyectos entre espacios de trabajo',
      blocks: [
        {
          kind: 'list',
          items: [
            'Utiliza **Exportar proyecto** para crear un paquete que incluya el proyecto junto con todos los archivos multimedia necesarios.',
            'Importa ese paquete en otro espacio de trabajo para continuar editando el mismo proyecto en otra carpeta o incluso en otro equipo.',
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'Los paquetes de proyecto son la forma más segura de compartir un trabajo, ya que incluyen tanto la línea de tiempo como todos los archivos multimedia vinculados.',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page